import React, { useEffect, useState } from 'react';
import { withRouter } from 'react-router-dom';
import { useQuery, useLazyQuery } from '@apollo/react-hooks';
import { graphql } from 'react-apollo';
import compose from 'lodash/flowRight';
import CURRENT_USER from '../../../../GraphQl/Queries/CURRENT_USER';
import OverlaySpinner from '../../../../../platform/shared/OverlaySpinner';
import ErrorPage from '../../../../../platform/shared/ErrorPage';
import CAMPAIGN_ORDER_SESSIONS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_SESSIONS';
import CAMPAIGN_ORDER_SESSIONS_CONNECTION from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_SESSIONS_CONNECTION';
import CAMPAIGN_ORDER_SESSIONS_ALL from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_SESSIONS_ALL';
import CAMPAIGN_ORDER_SESSION_DETAILS from '../../../../GraphQl/Queries/CAMPAIGN_ORDER_SESSION_DETAILS';

const SessionsLoader = (props) => {
  const {
    children,
    match,
    location,
    campaignOrder,
    setChartData,
  } = props;
  const { params } = match;
  const { id, pageNumber } = params;
  const urlParams = new URLSearchParams(location.search);
  const sessionReportId = campaignOrder?.reportDetails?.sessions?.id;

  //Maximum displayed transactions per page
  const pageSize = urlParams.get('pageSize') ? parseInt(urlParams.get('pageSize')) : 10;
  const currentPage = pageNumber ? Number(pageNumber) : 1;

  const [expandedRowsData, setExpandedRowsData] = useState({});
  const [currentDateRange, setCurrentDateRange] = useState(null);

  const buildSessionsWhereClause = () => {
    if (!sessionReportId) {
      return null;
    }

    const baseWhere = {
      campaignOrderSessionsDeviceReport: {
        id: sessionReportId,
      },
    };

    if (currentDateRange) {
      const [startDate, endDate] = currentDateRange;
      baseWhere.AND = [
        { end_gte: startDate.clone().startOf('day').toISOString() },
        { end_lte: endDate.clone().endOf('day').toISOString() },
      ];
    }

    return baseWhere;
  };

  //By Date and Summary Data
  const { data, loading, error } = useQuery(CAMPAIGN_ORDER_SESSIONS, {
    variables: {
      where: {
        id,
      },
    },
  });

  // Paginated Sessions for table
  const {
    data: sessionsList,
    loading: sessionsLoading,
    error: sessionsError,
  } = useQuery(CAMPAIGN_ORDER_SESSIONS_CONNECTION, {
    variables: {
      first: pageSize,
      skip: (currentPage - 1) * pageSize,
      where: buildSessionsWhereClause(),
      orderBy: `start_ASC`,
    },
    skip: !sessionReportId,
  });

  // Lazy query for CSV export (all sessions)
  const [
    getAllSessions,
    { data: allSessionsData, loading: allSessionsLoading, error: allSessionsError },
  ] = useLazyQuery(CAMPAIGN_ORDER_SESSIONS_ALL);

  // Lazy query for individual session details (for expanded rows)
  const [fetchSessionDetails, { loading: sessionDetailsLoading }] = useLazyQuery(
    CAMPAIGN_ORDER_SESSION_DETAILS,
    {
      onCompleted: (data) => {
        if (data?.campaignOrderSessionsWithEvents) {
          const { key, impressions, sessionJourney } = data.campaignOrderSessionsWithEvents;
          setExpandedRowsData((prev) => ({
            ...prev,
            [key]: { impressions, sessionJourney, loading: false },
          }));
        }
      },
    }
  );

  if (loading) return <OverlaySpinner />;
  if (error) return <ErrorPage error={error} />;
  if (!sessionReportId) {
    return <ErrorPage error={{ message: 'Session report data is not available for this campaign.' }} />;
  }

  return React.cloneElement(children, {
    ...children.props,
    sessions: data?.campaignOrder?.reportDetails?.sessions,
    sessionsList: sessionsList,
    loading: loading,
    error: error,
    sessionsError: sessionsError,
    sessionsLoading: sessionsLoading,
    getAllSessions: getAllSessions,
    allSessionsData: allSessionsData,
    allSessionsLoading: allSessionsLoading,
    allSessionsError: allSessionsError,
    fetchSessionDetails: fetchSessionDetails,
    expandedRowsData: expandedRowsData,
    setExpandedRowsData: setExpandedRowsData,
    sessionDetailsLoading: sessionDetailsLoading,
    currentDateRange: currentDateRange,
    setCurrentDateRange: setCurrentDateRange,
    buildSessionsWhereClause: buildSessionsWhereClause,
  });
};

export default compose(
  graphql(CURRENT_USER, {
    props: ({ data: { currentUser }, ownProps }) => {
      return ownProps.currentUser || { currentUser };
    },
  })
)(withRouter(SessionsLoader));
